import React, { useEffect, useRef } from "react";

import Button from "../components/Button";
import ContactForm from "../components/ContactForm";
import CustomCursor from "../components/CustomCursor";
import MessageLinks from "../components/MessageLinks";
import { track } from "../lib/contact";
import Nav from "../components/Nav";
import SpotlightCard from "../components/SpotlightCard";
import { cn } from "../lib/utils";

/* ── Scroll reveal hook ── */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e, i) => {
          if (e.isIntersecting) {
            setTimeout(
              () =>
                (e.target as HTMLElement).classList.add(
                  "opacity-100",
                  "translate-y-0",
                ),
              i * 60,
            );
          }
        });
      },
      { threshold: 0.07 },
    );
    el.querySelectorAll(".reveal").forEach((r) => obs.observe(r));
    return () => obs.disconnect();
  }, []);
  return ref;
}

/* ── Eyebrow label ── */
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="eyebrow mb-6 flex items-center gap-2.5 text-[10px] font-medium uppercase tracking-[0.18em] text-muted">
      <span className="inline-block h-px w-5 shrink-0 bg-lime" />
      {children}
    </div>
  );
}

/* ── Section title ── */
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-4 font-heading text-[clamp(28px,4vw,46px)] font-bold leading-[1.05] -tracking-[0.03em]">
      {children}
    </h2>
  );
}

export default function Home() {
  const pageRef = useReveal();

  // Arriving from another route (e.g. /solutions/* → /#contact): the browser's
  // native hash jump fires before React renders, so scroll once mounted.
  useEffect(() => {
    document
      .getElementById(window.location.hash.slice(1))
      ?.scrollIntoView({ behavior: "instant" as ScrollBehavior });
  }, []);

  return (
    <div ref={pageRef}>
      <CustomCursor />
      <Nav />

      <main>
        {/* ═══════════════ HERO ═══════════════ */}
        <div className="relative grid grid-rows-[1fr_auto] overflow-hidden border-b border-border pb-16 pt-20">
          {/* Gradient orbs */}
          <div className="pointer-events-none absolute -right-[100px] -top-[150px] h-[600px] w-[600px] rounded-full bg-[radial-gradient(circle,rgba(212,255,0,0.07)_0%,transparent_70%)]" />
          <div className="pointer-events-none absolute bottom-0 left-[10%] h-[500px] w-[500px] rounded-full bg-[radial-gradient(circle,rgba(255,92,58,0.05)_0%,transparent_70%)]" />
          <div className="container mx-auto px-6">
            <div className="flex flex-col justify-center">
              <div className="mb-10 inline-flex w-fit items-center gap-2 rounded-full border border-border bg-surface px-4 py-[0.4rem] text-[12px] font-normal text-muted">
                <span className="h-[6px] w-[6px] animate-blink rounded-full bg-[#3ECF8E]" />
                Sydney · Accepting new projects
              </div>

              <h1 className="mb-8 max-w-[900px] font-heading text-[clamp(42px,6.5vw,88px)] font-black leading-[1] -tracking-[0.03em]">
                We make your
                <br />
                business look
                <br />
                <span className="font-serif font-normal italic -tracking-[0.01em] text-lime">
                  and work
                </span>{" "}
                better.
              </h1>

              <div className="flex flex-col gap-8 laptop:flex-row laptop:items-end laptop:justify-between laptop:gap-12">
                <p className="max-w-[420px] text-[15px] font-light leading-[1.75] text-muted">
                  lab.radical is a boutique studio for{" "}
                  <strong className="font-medium text-text">
                    design and automation
                  </strong>
                  . We build beautiful websites and apps — and wire up the
                  systems behind them so you spend less time on admin and more
                  time on what matters.
                </p>
                <div className="flex shrink-0 flex-col gap-2.5 laptop:flex-row">
                  <Button href="#contact">Let's talk ↗</Button>
                  <Button variant="ghost" href="#services">
                    See what we do
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="container mx-auto px-6">
            <SpotlightCard className="relative mt-16 grid grid-cols-2 overflow-hidden rounded-[14px] border border-border laptop:grid-cols-4">
              <SpotlightCard tilt={0} className="h-full">
                <div className="bg-bg2 p-6">
                  <span className="block font-heading text-[28px] font-bold -tracking-[0.03em] text-text">
                    2-in-1
                  </span>
                  <span className="text-[12px] tracking-wide text-muted">
                    Design & automation under one roof
                  </span>
                </div>
              </SpotlightCard>
              <SpotlightCard tilt={0} className="h-full">
                <div className="h-full bg-bg2 p-6">
                  <span className="block font-heading text-[28px] font-bold -tracking-[0.03em] text-text">
                    Fixed
                  </span>
                  <span className="text-[12px] tracking-wide text-muted">
                    Pricing agreed before we start
                  </span>
                </div>
              </SpotlightCard>
              <SpotlightCard tilt={0} className="h-full">
                <div className="h-full bg-bg2 p-6">
                  <span className="block font-heading text-[28px] font-bold -tracking-[0.03em] text-text">
                    15+ yrs
                  </span>
                  <span className="text-[12px] tracking-wide text-muted">
                    Experience across design & engineering
                  </span>
                </div>
              </SpotlightCard>
              <SpotlightCard tilt={0} className="h-full">
                <div className="h-full bg-bg2 p-6">
                  <span className="block font-heading text-[28px] font-bold -tracking-[0.03em] text-text">
                    Plain
                  </span>
                  <span className="text-[12px] tracking-wide text-muted">
                    English — no jargon, no fluff
                  </span>
                </div>
              </SpotlightCard>
            </SpotlightCard>
          </div>
        </div>

        {/* ═══════════════ TICKER ═══════════════ */}
        <div className="flex h-12 items-center overflow-hidden border-b border-border bg-surface">
          <div className="flex w-max animate-ticker gap-12">
            {[
              "Web Design",
              "App Development",
              "Brand Identity",
              "Workflow Automation",
              "Webflow",
              "Shopify",
              "Custom Integrations",
              "UI/UX Design",
              "Web Design",
              "App Development",
              "Brand Identity",
              "Workflow Automation",
              "Webflow",
              "Shopify",
              "Custom Integrations",
              "UI/UX Design",
            ].map((item, i) => (
              <span
                key={i}
                className={cn(
                  "flex items-center gap-2.5 whitespace-nowrap text-[12px] font-medium uppercase tracking-[0.1em] text-muted",
                  "hover:font-serif hover:text-base hover:lowercase hover:italic hover:tracking-wide hover:text-lime",
                )}
              >
                {item}
                <span className="text-[8px] text-lime">✦</span>
              </span>
            ))}
          </div>
        </div>

        {/* ═══════════════ SERVICES ═══════════════ */}
        <section
          id="services"
          className="reveal border-b border-border py-24 opacity-0 transition-all duration-[0.65s]"
        >
          <div className="container mx-auto px-6">
            <div className="mb-14 flex flex-col items-start justify-between gap-6 laptop:flex-row laptop:items-end">
              <div>
                <Eyebrow>What we do</Eyebrow>
                <SectionTitle>
                  Two capabilities.
                  <br />
                  <em className="font-serif text-[1.05em] font-normal italic text-muted">
                    One studio.
                  </em>
                </SectionTitle>
              </div>
              <a
                href="mailto:hello@labradical.com.au"
                className="link-sm flex items-center gap-1.5 pb-2 text-[12px] font-medium text-muted no-underline transition-colors hover:text-text"
              >
                Talk to us about your project ↗
              </a>
            </div>

            <SpotlightCard className="overflow-hidden rounded-[14px] border border-border">
              <div className="grid grid-cols-1 laptop:grid-cols-2">
                {/* Design column */}
                <SpotlightCard tilt={0}>
                  <div className="group bg-bg2 p-10">
                    <div
                      className={cn(
                        "bg-blue/12 mb-8 w-fit rounded-full border border-blue/20 px-3.5 py-[0.35rem] font-heading text-[10px] font-normal uppercase tracking-[0.16em] text-blue-text transition-all duration-300",
                        "group-hover:animate-pulse group-hover:bg-blue group-hover:text-black",
                      )}
                    >
                      Design
                    </div>
                    <h3 className="mb-4 font-heading text-[22px] font-bold leading-snug -tracking-[0.03em]">
                      Things that look
                      <br />
                      impossible to ignore.
                    </h3>
                    <p className="mb-8 text-[14px] font-light leading-snug text-muted">
                      Websites, apps, and brand identities that are sharp,
                      intentional, and built to make an impression — whether
                      you're just starting out or growing fast.
                    </p>
                    <ul className="flex flex-col">
                      {[
                        ["Website design & development", "Webflow, Next.js"],
                        ["Mobile app UI/UX", "iOS & Android"],
                        ["Brand identity & logo", "Full system"],
                        ["E-commerce stores", "Shopify, Stripe, WooCommerce"],
                        ["Landing pages", "High-converting"],
                      ].map(([label, tag], i) => (
                        <li
                          key={i}
                          className="flex items-center justify-between border-t border-border py-3 text-[14px] text-text"
                        >
                          {label}
                          <span className="text-[12px] text-muted">{tag}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </SpotlightCard>

                {/* Dev column */}
                <SpotlightCard tilt={0}>
                  <div className="group bg-bg2 p-10">
                    <div
                      className={cn(
                        "bg-lime/8 mb-8 w-fit rounded-full border border-lime/15 px-3.5 py-[0.35rem] font-heading text-[10px] font-normal uppercase tracking-[0.16em] text-lime",
                        "group-hover:animate-pulse group-hover:bg-lime group-hover:text-black",
                      )}
                    >
                      Dev & Automation
                    </div>
                    <h3 className="mb-4 font-heading text-[22px] font-bold leading-snug -tracking-[0.03em]">
                      Systems that work
                      <br />
                      while you sleep.
                    </h3>
                    <p className="mb-8 text-[14px] font-light leading-snug text-muted">
                      We connect the tools you already use, automate the boring
                      stuff, and build small custom apps that make your team
                      wonder how they managed without them.
                    </p>
                    <ul className="flex flex-col">
                      {[
                        ["Workflow automation", "Make, Zapier, n8n"],
                        ["Custom web tools & dashboards", "Internal apps"],
                        ["App integrations", "API connections"],
                        ["Form & booking systems", "No more WhatsApp"],
                        ["CRM & reporting setup", "Airtable, Notion"],
                      ].map(([label, tag], i) => (
                        <li
                          key={i}
                          className="flex items-center justify-between border-t border-border py-3 text-[14px] text-text"
                        >
                          {label}
                          <span className="text-[12px] text-muted">{tag}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </SpotlightCard>
              </div>
            </SpotlightCard>
          </div>
        </section>

        {/* ═══════════════ SUBSCRIPTION SWAP ═══════════════ */}
        <section
          id="swap"
          className="reveal border-b border-border py-24 opacity-0 transition-all duration-[0.65s]"
        >
          <div className="container mx-auto grid gap-12 px-6 laptop:grid-cols-2 laptop:gap-20">
            <div>
              <Eyebrow>AI search visibility</Eyebrow>
              <SectionTitle>
                Stop paying templates.
                <br />
                <em className="font-serif text-[1.05em] font-normal italic text-muted">
                  Start funding an asset.
                </em>
              </SectionTitle>
              <p className="mb-8 text-[14px] font-light leading-[1.75] text-muted">
                Traditional SEO ranks you on Google's blue links. GEO makes you
                the answer ChatGPT, Perplexity and Apple Intelligence recommend.
                We move dated Squarespace, Wix and WordPress sites onto fast
                Next.js edge infrastructure with machine-readable menu, business
                and FAQ schema built in.
              </p>
              <div className="mb-8 flex flex-wrap gap-x-5 gap-y-2 text-[12px] text-muted">
                {[
                  ["hospitality", "Restaurants & cafes"],
                  ["dental", "Dental clinics"],
                  ["clinics", "Aesthetics clinics"],
                ].map(([slug, label]) => (
                  <a
                    key={slug}
                    href={`/solutions/${slug}`}
                    className="text-text underline-offset-4 hover:underline"
                  >
                    {label} →
                  </a>
                ))}
              </div>
              <Button
                href="#contact"
                className="!w-fit"
                onClick={() => track("cta_click", { where: "home_swap" })}
              >
                Get your site rebuilt ↗
              </Button>
              <div className="mt-5">
                <MessageLinks where="home_swap" />
              </div>
            </div>

            <SpotlightCard className="flex flex-col overflow-hidden rounded-[14px] border border-border">
              {[
                [
                  "01",
                  "Cancel the builder",
                  "Drop your Squarespace or Wix plan and any separate hosting. We aim to match what you pay now, combined, plus a one-off setup fee quoted up front.",
                ],
                [
                  "02",
                  "We rebuild on the edge",
                  "Next.js + Turso, with LocalBusiness, FoodMenu and FAQPage schema so AI engines can cite you.",
                ],
                [
                  "03",
                  "Text us a photo",
                  "A menu change or new opening hours? Send a photo or message and we publish it the same day.",
                ],
              ].map(([num, title, desc], i) => (
                <div
                  key={i}
                  className="grid cursor-default gap-5 border-b border-border bg-bg2 px-8 py-7 transition-colors last:border-none hover:bg-surface"
                  style={{ gridTemplateColumns: "3rem 1fr" }}
                >
                  <span className="pt-0.5 font-heading text-[12px] font-bold text-lime opacity-50">
                    {num}
                  </span>
                  <div>
                    <div className="mb-[0.35rem] text-[14px] font-bold -tracking-[0.01em]">
                      {title}
                    </div>
                    <p className="text-[12px] font-light leading-[1.6] text-muted">
                      {desc}
                    </p>
                  </div>
                </div>
              ))}
            </SpotlightCard>
          </div>
        </section>

        {/* ═══════════════ HOW ═══════════════ */}
        <section
          id="how"
          className="reveal border-b border-border py-24 opacity-0 transition-all duration-[0.65s]"
        >
          <div className="container mx-auto grid gap-12 px-6 laptop:grid-cols-2 laptop:gap-20">
            <div>
              <Eyebrow>How it works</Eyebrow>
              <SectionTitle>
                Simple process.
                <br />
                <em className="font-serif text-[1.05em] font-normal italic text-muted">
                  Real results.
                </em>
              </SectionTitle>
              <p className="mb-8 text-[14px] font-light leading-[1.75] text-muted">
                No mystery scopes. No disappearing acts. We keep things
                transparent, move fast, and make sure you understand everything
                that's being built — before, during and after.
              </p>
              <Button href="#contact" className="!w-fit">
                Book a free chat ↗
              </Button>
            </div>

            <SpotlightCard className="flex flex-col overflow-hidden rounded-[14px] border border-border">
              {[
                [
                  "01",
                  "Free discovery call",
                  "20 minutes. Tell us what you're building or what's broken. No prep needed — just a conversation.",
                ],
                [
                  "02",
                  "Scope & proposal",
                  "We send a clear write-up: what we'll build, how long it'll take, and a fixed price. No surprises.",
                ],
                [
                  "03",
                  "Design & build",
                  "You're in the loop at every stage. We share progress, gather feedback, and refine until it's right.",
                ],
                [
                  "04",
                  "Launch & handover",
                  "We go live, run QA together, and hand over docs and walk-through videos so you own it completely.",
                ],
              ].map(([num, title, desc], i) => (
                <div
                  key={i}
                  className="grid cursor-default gap-5 border-b border-border bg-bg2 px-8 py-7 transition-colors last:border-none hover:bg-surface"
                  style={{ gridTemplateColumns: "3rem 1fr" }}
                >
                  <span className="pt-0.5 font-heading text-[12px] font-bold text-lime opacity-50">
                    {num}
                  </span>
                  <div>
                    <div className="mb-[0.35rem] text-[14px] font-bold -tracking-[0.01em]">
                      {title}
                    </div>
                    <p className="text-[12px] font-light leading-[1.6] text-muted">
                      {desc}
                    </p>
                  </div>
                </div>
              ))}
            </SpotlightCard>
          </div>
        </section>

        {/* ═══════════════ WHO ═══════════════ */}
        <section
          id="who"
          className="reveal border-b border-border py-24 opacity-0 transition-all duration-[0.65s]"
        >
          <div className="container mx-auto px-6">
            <Eyebrow>Who we help</Eyebrow>
            <SectionTitle>
              Built for businesses
              <br />
              <em className="font-serif text-[1.05em] font-normal italic text-muted">
                ready to level up.
              </em>
            </SectionTitle>

            <div className="mt-14 grid gap-3 laptop:grid-cols-3">
              {[
                {
                  icon: "01 / Service Businesses",
                  title: "Clinics, trades & coaches",
                  desc: 'Drowning in booking chaos, email threads and "did anyone follow up on that?" We build you a system that runs itself.',
                  tags: [
                    "Allied health",
                    "Trades & construction",
                    "Coaching & consulting",
                  ],
                },
                {
                  icon: "02 / Shops & Studios",
                  title: "Retail, salons & gyms",
                  desc: "Managing stock, clients and payments across five apps. We bring it together — and make it look great in the process.",
                  tags: [
                    "Retail & e-commerce",
                    "Salons & beauty",
                    "Gyms & studios",
                  ],
                },
                {
                  icon: "03 / Startups & Founders",
                  title: "Early-stage & growing fast",
                  desc: "Need to look credible on day one and move without a full team? We're your design and dev studio on demand.",
                  tags: [
                    "SaaS & apps",
                    "Consumer products",
                    "Marketplace & platforms",
                  ],
                },
              ].map((card, i) => (
                <SpotlightCard
                  key={i}
                  className="rounded-[14px] border border-border bg-surface p-8 transition-all duration-[0.25s] hover:-translate-y-0.5 hover:border-lime/20"
                >
                  <span className="mb-6 block font-heading text-[12px] font-bold tracking-[0.1em] text-lime opacity-50">
                    {card.icon}
                  </span>
                  <div className="mb-[0.6rem] text-[16px] font-bold -tracking-[0.02em]">
                    {card.title}
                  </div>
                  <p className="text-[14px] font-light leading-[1.65] tracking-wide text-muted">
                    {card.desc}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-1.5">
                    {card.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-border px-[0.65rem] py-[0.2rem] text-[12px] text-muted"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </SpotlightCard>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════ PORTFOLIO ═══════════════ */}
        <section
          id="portfolio"
          className="reveal border-b border-border py-24 opacity-0 transition-all duration-[0.65s]"
        >
          <div className="container mx-auto px-6">
            <Eyebrow>Portfolio</Eyebrow>
            <SectionTitle>
              Products we have built.
              <br />
              <em className="font-serif text-[1.05em] font-normal italic text-muted">
                Yours could be next.
              </em>
            </SectionTitle>

            <div className="mt-14 grid gap-6 laptop:grid-cols-2">
              {/* SiteChat */}
              <SpotlightCard className="rounded-[14px] border border-border bg-surface transition-all duration-[0.25s] hover:-translate-y-0.5 hover:border-lime/20">
                <a
                  href="https://sitechat.labradical.com.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block cursor-pointer"
                >
                  <div className="flex aspect-[16/9] items-center justify-center border-b border-border bg-bg2 p-8">
                    <div className="flex h-full w-full items-center justify-center rounded-lg border border-border/40 bg-black/30">
                      <span className="font-heading text-[clamp(18px,3vw,32px)] font-black tracking-tight text-lime">
                        SiteChat
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="inline-block rounded-full bg-lime/10 px-2.5 py-[0.15rem] text-[10px] font-medium uppercase tracking-[0.12em] text-lime">
                        Live product
                      </span>
                      <span className="inline-block rounded-full bg-blue/10 px-2.5 py-[0.15rem] text-[10px] font-medium uppercase tracking-[0.12em] text-blue-text">
                        SaaS
                      </span>
                    </div>
                    <h3 className="mb-1.5 text-[16px] font-bold -tracking-[0.02em]">
                      SiteChat
                    </h3>
                    <p className="mb-4 text-[13px] font-light leading-[1.6] text-muted">
                      AI chatbot widget for any website. Paste one script tag,
                      it reads your live site and answers customers
                      automatically.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {["Next.js", "Supabase", "Stripe", "AI"].map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full border border-border px-[0.55rem] py-[0.15rem] text-[11px] text-muted"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </a>
              </SpotlightCard>

              {/* PocketPlay */}
              <SpotlightCard className="rounded-[14px] border border-border bg-surface transition-all duration-[0.25s] hover:-translate-y-0.5 hover:border-lime/20">
                <a
                  href="https://pocketplay.labradical.com.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block cursor-pointer"
                >
                  <div className="flex aspect-[16/9] items-center justify-center border-b border-border bg-bg2 p-8">
                    <div className="flex h-full w-full items-center justify-center rounded-lg border border-border/40 bg-black/30">
                      <span className="font-heading text-[clamp(18px,3vw,32px)] font-black tracking-tight text-amber-300">
                        PocketPlay
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="inline-block rounded-full bg-lime/10 px-2.5 py-[0.15rem] text-[10px] font-medium uppercase tracking-[0.12em] text-lime">
                        Live product
                      </span>
                      <span className="inline-block rounded-full bg-coral/10 px-2.5 py-[0.15rem] text-[10px] font-medium uppercase tracking-[0.12em] text-coral">
                        PWA
                      </span>
                    </div>
                    <h3 className="mb-1.5 text-[16px] font-bold -tracking-[0.02em]">
                      PocketPlay
                    </h3>
                    <p className="mb-4 text-[13px] font-light leading-[1.6] text-muted">
                      Digital busy book for kids 2-8. Beautiful, no-mess
                      activities always on your phone. Perfect for restaurants,
                      queues, and quiet time.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {["Next.js", "PWA", "Stripe", "Tailwind"].map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full border border-border px-[0.55rem] py-[0.15rem] text-[11px] text-muted"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </a>
              </SpotlightCard>

              {/* Pocket Ledger */}
              <SpotlightCard className="rounded-[14px] border border-border bg-surface transition-all duration-[0.25s] hover:-translate-y-0.5 hover:border-lime/20">
                <a
                  href="https://pocketledger.labradical.com.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block cursor-pointer"
                >
                  <div className="flex aspect-[16/9] items-center justify-center border-b border-border bg-bg2 p-8">
                    <div className="flex h-full w-full items-center justify-center rounded-lg border border-border/40 bg-black/30">
                      <span className="font-heading text-[clamp(18px,3vw,32px)] font-black tracking-tight text-teal-400">
                        Pocket Ledger
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="inline-block rounded-full bg-lime/10 px-2.5 py-[0.15rem] text-[10px] font-medium uppercase tracking-[0.12em] text-lime">
                        Live product
                      </span>
                      <span className="inline-block rounded-full bg-coral/10 px-2.5 py-[0.15rem] text-[10px] font-medium uppercase tracking-[0.12em] text-coral">
                        PWA
                      </span>
                    </div>
                    <h3 className="mb-1.5 text-[16px] font-bold -tracking-[0.02em]">
                      Pocket Ledger
                    </h3>
                    <p className="mb-4 text-[13px] font-light leading-[1.6] text-muted">
                      Digital allowance for Australian kids (5-12). No bank card
                      required — chores, savings goals, and pocket money in one
                      simple app.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {["Next.js", "Firebase", "Stripe", "Tailwind"].map(
                        (tag) => (
                          <span
                            key={tag}
                            className="rounded-full border border-border px-[0.55rem] py-[0.15rem] text-[11px] text-muted"
                          >
                            {tag}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                </a>
              </SpotlightCard>

              {/* CLYNN by Nature */}
              <SpotlightCard className="rounded-[14px] border border-border bg-surface transition-all duration-[0.25s] hover:-translate-y-0.5 hover:border-lime/20">
                <a
                  href="https://clynnbynature.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block cursor-pointer"
                >
                  <div className="flex aspect-[16/9] items-center justify-center border-b border-border bg-bg2 p-8">
                    <div className="flex h-full w-full items-center justify-center rounded-lg border border-border/40 bg-black/30">
                      <span className="font-heading text-[clamp(18px,3vw,32px)] font-black tracking-tight text-emerald-400">
                        CLYNN
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="inline-block rounded-full bg-lime/10 px-2.5 py-[0.15rem] text-[10px] font-medium uppercase tracking-[0.12em] text-lime">
                        Live product
                      </span>
                      <span className="inline-block rounded-full bg-coral/10 px-2.5 py-[0.15rem] text-[10px] font-medium uppercase tracking-[0.12em] text-coral">
                        E-commerce
                      </span>
                    </div>
                    <h3 className="mb-1.5 text-[16px] font-bold -tracking-[0.02em]">
                      CLYNN by Nature
                    </h3>
                    <p className="mb-4 text-[13px] font-light leading-[1.6] text-muted">
                      Organic hair care e‑commerce brand built from scratch.
                      Fully custom product catalogue, real‑time shipping rates,
                      dual payment gateway, and automated order management.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {["Next.js", "Stripe", "Firebase", "Tailwind"].map(
                        (tag) => (
                          <span
                            key={tag}
                            className="rounded-full border border-border px-[0.55rem] py-[0.15rem] text-[11px] text-muted"
                          >
                            {tag}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                </a>
              </SpotlightCard>

              {/* Revel */}
              <SpotlightCard className="rounded-[14px] border border-border bg-surface transition-all duration-[0.25s] hover:-translate-y-0.5 hover:border-lime/20">
                <a
                  href="https://revel.labradical.com.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block cursor-pointer"
                >
                  <div className="flex aspect-[16/9] items-center justify-center border-b border-border bg-bg2 p-8">
                    <div className="flex h-full w-full items-center justify-center rounded-lg border border-border/40 bg-black/30">
                      <span className="font-heading text-[clamp(18px,3vw,32px)] font-black tracking-tight text-rose-400">
                        Revel
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="inline-block rounded-full bg-lime/10 px-2.5 py-[0.15rem] text-[10px] font-medium uppercase tracking-[0.12em] text-lime">
                        Live product
                      </span>
                      <span className="inline-block rounded-full bg-blue/10 px-2.5 py-[0.15rem] text-[10px] font-medium uppercase tracking-[0.12em] text-blue-text">
                        SaaS
                      </span>
                    </div>
                    <h3 className="mb-1.5 text-[16px] font-bold -tracking-[0.02em]">
                      Revel
                    </h3>
                    <p className="mb-4 text-[13px] font-light leading-[1.6] text-muted">
                      Party and event planning made simple. Guest management,
                      RSVPs, wishlists, food coordination, and budgeting — all
                      in one place.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {["Next.js", "Supabase", "Stripe", "Tailwind"].map(
                        (tag) => (
                          <span
                            key={tag}
                            className="rounded-full border border-border px-[0.55rem] py-[0.15rem] text-[11px] text-muted"
                          >
                            {tag}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                </a>
              </SpotlightCard>
            </div>

            <div className="mt-10 text-center">
              <Button href="#contact">Build your product too ↗</Button>
            </div>
          </div>
        </section>

        {/* ═══════════════ ABOUT ═══════════════ */}
        <section
          id="about"
          className="reveal border-b border-border py-24 opacity-0 transition-all duration-[0.65s]"
        >
          <div className="container mx-auto grid gap-12 px-6 laptop:grid-cols-2 laptop:items-center laptop:gap-20">
            <div>
              <Eyebrow>The studio</Eyebrow>
              <h2 className="mb-6 mt-4 font-heading text-[clamp(28px,3.5vw,42px)] font-bold leading-[1.1] -tracking-[0.03em]">
                Boutique by design.
                <br />
                <em className="font-serif font-normal italic text-lime">
                  Effective by obsession.
                </em>
              </h2>
              <p className="mb-8 text-[14px] font-light leading-[1.8] tracking-wide text-muted">
                lab.radical is a small, deliberate studio — not an agency with
                40 clients and a junior doing your work. Every project gets{" "}
                <strong className="font-medium text-text">
                  senior attention
                </strong>
                , sharp thinking, and the kind of craft that makes a difference.
                <br />
                <br />
                We're designers who can build, and builders who care deeply
                about design. That combination is rarer than it should be — and
                it means{" "}
                <strong className="font-medium text-text">
                  nothing falls between the cracks
                </strong>
                .
              </p>
              <div className="flex w-fit items-center gap-3.5 rounded-full border border-border bg-surface px-5 py-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,#FF5C3A_0%,#4B6FFF_100%)] font-heading text-[12px] font-bold text-white">
                  CY
                </div>
                <div>
                  <div className="text-[14px] font-semibold">Chris Yap</div>
                  <div className="text-[12px] font-light text-muted">
                    Founder · Designer & Engineer · Sydney, AU
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <SpotlightCard className="rounded-[14px] border border-lime/15 bg-surface p-6 leading-tight">
                <span className="mb-2 block font-heading text-[32px] font-bold -tracking-[0.03em] text-lime">
                  Design
                </span>
                <span className="text-[12px] font-light tracking-wide text-muted">
                  + dev/automation in one studio. No handoff headaches.
                </span>
              </SpotlightCard>
              <SpotlightCard className="rounded-[14px] border border-coral/15 bg-surface p-6 leading-tight">
                <span className="mb-2 block font-heading text-[32px] font-bold -tracking-[0.03em] text-coral">
                  Fixed
                </span>
                <span className="text-[12px] font-light tracking-wide text-muted">
                  pricing. Agreed upfront, no billable-hour surprises.
                </span>
              </SpotlightCard>
              <SpotlightCard className="rounded-[14px] border border-blue/20 bg-surface p-6 leading-tight">
                <span className="mb-2 block font-heading text-[32px] font-bold -tracking-[0.03em] text-blue">
                  20 min
                </span>
                <span className="text-[12px] font-light tracking-wide text-muted">
                  free discovery call. No pitch deck. Just a conversation.
                </span>
              </SpotlightCard>
              <SpotlightCard className="col-span-2 rounded-[14px] bg-surface p-6 leading-tight laptop:col-span-1">
                <span className="mb-2 block text-[clamp(18px,2vw,24px)] font-bold -tracking-[0.02em] text-text">
                  Sydney-based.
                </span>
                <span className="mt-1 block text-[clamp(18px,2vw,24px)] text-xl font-light">
                  Working with great businesses everywhere.
                </span>
              </SpotlightCard>
            </div>
          </div>
        </section>

        {/* ═══════════════ CTA ═══════════════ */}
        <div className="reveal border-b border-border py-10 opacity-0 transition-all duration-[0.65s]">
          <div className="container mx-auto px-6">
            <div className="relative overflow-hidden rounded-[20px] bg-lime px-6 py-16 laptop:grid laptop:grid-cols-[1fr_auto] laptop:items-center laptop:gap-12 laptop:px-12">
              <div className="pointer-events-none absolute -bottom-10 -right-5 select-none font-heading text-[180px] font-black leading-[1] -tracking-[0.04em] text-black/5">
                →
              </div>
              <div className="relative z-10">
                <h2 className="font-heading text-[clamp(28px,4vw,48px)] font-black leading-[1.05] -tracking-[0.03em] text-bg">
                  Got something
                  <br />
                  in <em className="font-serif font-normal italic">mind?</em>
                </h2>
                <p className="relative z-10 mt-3 text-[14px] font-light text-bg/60">
                  Tell us what you're building, what's broken, or what you wish
                  existed. We'll tell you honestly if we can help.
                </p>
              </div>
              <div className="relative z-10 mt-8 flex shrink-0 flex-col gap-2.5 laptop:mt-0">
                <Button variant="dark" href="#contact">
                  Start a project ↗
                </Button>
                <Button variant="outline" href="mailto:hello@labradical.com.au">
                  Or email us →
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════════ CONTACT FORM ═══════════════ */}
        <ContactForm />
      </main>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto flex flex-col items-start justify-between gap-6 px-6 laptop:flex-row">
          <a
            href="/"
            className="font-heading text-[14px] font-bold -tracking-[0.02em] text-text no-underline"
          >
            lab<span className="text-lime">.</span>
            <span className="font-serif text-[18px] italic text-lime">
              radical
            </span>
          </a>
          <div className="flex flex-col gap-4 laptop:flex-row laptop:gap-8">
            {/* <a
              href="https://instagram.com"
              className="text-[12px] text-muted no-underline transition-colors hover:text-text"
            >
              Instagram
            </a>
            <a
              href="https://linkedin.com"
              className="text-[12px] text-muted no-underline transition-colors hover:text-text"
            >
              LinkedIn
            </a> */}
            <a
              href="/faq"
              className="text-[12px] text-muted no-underline transition-colors hover:text-text"
            >
              FAQ
            </a>
            <a
              href="mailto:hello@labradical.com.au"
              className="text-[12px] text-muted no-underline transition-colors hover:text-text"
            >
              hello@labradical.com.au
            </a>
          </div>
          <div className="text-[12px] text-muted">
            © 2026 lab.radical · Sydney, AU
          </div>
        </div>
      </footer>
    </div>
  );
}
